// src/pages/common-components/Auth/Account.tsx
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../Header/Header';
import HeaderSpacer from '../Header/HeaderSpacer';
import Footer from '../Footer/Footer';
import BackToTop from '../Back-to-top/BackToTop';
import { useAuth } from '../../Contexts/AuthContext';
import { updateProfile } from 'firebase/auth';
import { updateCustomerProfile, uploadCustomerProfilePhoto } from '../../Services/customerService';
import { fetchMyReservations, ReservationRecord } from '../../Services/reservationService';
import { fetchMyTradeIns, TradeInRecord } from '../../Services/tradeInService';

type Tab = 'requests' | 'tradeIns';

const Account: React.FC = () => {
  const { currentUser, customerProfile, loading, logout, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  const [editing, setEditing] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [phoneDraft, setPhoneDraft] = useState('');
  const [saving, setSaving] = useState(false);

  const [tab, setTab] = useState<Tab>('requests');
  const [requests, setRequests] = useState<ReservationRecord[] | null>(null);
  const [tradeIns, setTradeIns] = useState<TradeInRecord[] | null>(null);
  const [activityLoading, setActivityLoading] = useState(false);

  useEffect(() => {
    if (!loading && !currentUser) {
      navigate('/login', { replace: true, state: { from: '/account' } });
    }
  }, [loading, currentUser, navigate]);

  useEffect(() => {
    if (!currentUser) return;
    setActivityLoading(true);
    Promise.all([fetchMyReservations(currentUser.uid), fetchMyTradeIns(currentUser.uid)])
      .then(([r, t]) => {
        setRequests(r);
        setTradeIns(t);
      })
      .catch((err) => console.error('Error loading account activity:', err))
      .finally(() => setActivityLoading(false));
  }, [currentUser]);

  const startEditing = () => {
    setNameDraft(customerProfile?.fullName || currentUser?.displayName || '');
    setPhoneDraft(customerProfile?.phone || '');
    setEditing(true);
  };

  const handleSaveProfile = async () => {
    if (!currentUser || !nameDraft.trim()) return;
    setSaving(true);
    try {
      await updateCustomerProfile(currentUser.uid, { fullName: nameDraft.trim(), phone: phoneDraft.trim() });
      if (currentUser.displayName !== nameDraft.trim()) {
        await updateProfile(currentUser, { displayName: nameDraft.trim() });
      }
      await refreshProfile();
      setEditing(false);
    } catch (err) {
      console.error('Error saving profile:', err);
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !currentUser) return;
    if (!file.type.startsWith('image/')) {
      setUploadError('Please choose an image file.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setUploadError('Image must be smaller than 5MB.');
      return;
    }
    setUploadError('');
    setUploading(true);
    try {
      const photoURL = await uploadCustomerProfilePhoto(currentUser.uid, file);
      await updateCustomerProfile(currentUser.uid, { photoURL });
      await updateProfile(currentUser, { photoURL });
      await refreshProfile();
    } catch (err) {
      console.error('Error uploading profile photo:', err);
      setUploadError('Failed to upload photo. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/', { replace: true });
  };

  if (loading || !currentUser) {
    return (
      <div>
        <Header />
        <HeaderSpacer />
        <div style={{ textAlign: 'center', padding: '80px 15px' }}>Loading…</div>
        <Footer />
      </div>
    );
  }

  const displayName = customerProfile?.fullName || currentUser.displayName || 'there';
  const photoURL = customerProfile?.photoURL || currentUser.photoURL || '';
  const initial = displayName.charAt(0).toUpperCase() || '?';

  return (
    <div>
      <Header />
      <HeaderSpacer />

      <div className="container" style={{ maxWidth: 900, padding: '50px 15px 90px' }}>
        {/* ── Profile header ─────────────────────────────────────────── */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap',
          border: '1px solid #eee', borderRadius: 12, padding: '28px 26px', marginBottom: 26,
          background: 'linear-gradient(135deg, #f6f7ff 0%, #fff 100%)',
        }}>
          <div style={{ position: 'relative', flexShrink: 0 }}>
            <div style={{
              width: 84, height: 84, borderRadius: '50%', overflow: 'hidden',
              background: '#717fe0', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 32, fontWeight: 700, border: '3px solid #fff', boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
            }}>
              {photoURL ? (
                <img src={photoURL} alt={displayName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : initial}
            </div>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              aria-label="Change profile picture"
              style={{
                position: 'absolute', bottom: -2, right: -2, width: 30, height: 30, borderRadius: '50%',
                background: '#222', color: '#fff', border: '2px solid #fff', cursor: uploading ? 'wait' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14,
              }}
            >
              {uploading ? '…' : <i className="zmdi zmdi-camera" />}
            </button>
            <input ref={fileInputRef} type="file" accept="image/*" hidden onChange={handlePhotoSelected} />
          </div>

          <div style={{ flex: 1, minWidth: 200 }}>
            <h4 style={{ margin: '0 0 4px' }}>Welcome, {displayName}</h4>
            <p style={{ margin: 0, color: '#888', fontSize: 14 }}>{customerProfile?.email || currentUser.email}</p>
            {uploadError && <p style={{ margin: '6px 0 0', color: '#c0392b', fontSize: 13 }}>{uploadError}</p>}
          </div>

          <button
            type="button"
            onClick={handleLogout}
            style={{
              padding: '10px 22px', background: '#fff', color: '#222', border: '1px solid #ddd',
              borderRadius: 100, fontSize: 13, fontWeight: 600, cursor: 'pointer', flexShrink: 0,
            }}
          >
            Sign Out
          </button>
        </div>

        {/* ── Profile details ────────────────────────────────────────── */}
        <div style={{ border: '1px solid #eee', borderRadius: 12, padding: '24px 26px', marginBottom: 26 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h5 style={{ margin: 0, fontSize: 15 }}>Profile Details</h5>
            {!editing && (
              <button
                type="button"
                onClick={startEditing}
                style={{ background: 'none', border: 'none', color: '#717fe0', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
              >
                Edit
              </button>
            )}
          </div>

          {editing ? (
            <div style={{ display: 'grid', gap: 14, maxWidth: 420 }}>
              <div>
                <label style={{ display: 'block', fontSize: 13, marginBottom: 5, color: '#666' }}>Full Name</label>
                <input
                  value={nameDraft}
                  onChange={(e) => setNameDraft(e.target.value)}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: 4, fontSize: 16, boxSizing: 'border-box' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 13, marginBottom: 5, color: '#666' }}>Phone</label>
                <input
                  value={phoneDraft}
                  onChange={(e) => setPhoneDraft(e.target.value)}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: 4, fontSize: 16, boxSizing: 'border-box' }}
                />
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  type="button"
                  onClick={handleSaveProfile}
                  disabled={saving || !nameDraft.trim()}
                  style={{ padding: '9px 20px', background: '#717fe0', color: '#fff', border: 'none', borderRadius: 4, fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: saving ? 0.7 : 1 }}
                >
                  {saving ? 'Saving…' : 'Save Changes'}
                </button>
                <button
                  type="button"
                  onClick={() => setEditing(false)}
                  disabled={saving}
                  style={{ padding: '9px 20px', background: '#fff', color: '#222', border: '1px solid #ddd', borderRadius: 4, fontSize: 13, cursor: 'pointer' }}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div>
              <Row label="Name" value={customerProfile?.fullName || currentUser.displayName || '—'} />
              <Row label="Email" value={customerProfile?.email || currentUser.email || '—'} />
              <Row label="Phone" value={customerProfile?.phone || '—'} />
            </div>
          )}
        </div>

        {/* ── Activity ────────────────────────────────────────────────── */}
        <div style={{ border: '1px solid #eee', borderRadius: 12, padding: '24px 26px' }}>
          <h5 style={{ margin: '0 0 16px', fontSize: 15 }}>My Activity</h5>

          <div style={{ display: 'flex', gap: 8, marginBottom: 18, borderBottom: '1px solid #eee' }}>
            <TabButton active={tab === 'requests'} onClick={() => setTab('requests')}>
              Reservations &amp; Test Drives {requests ? `(${requests.length})` : ''}
            </TabButton>
            <TabButton active={tab === 'tradeIns'} onClick={() => setTab('tradeIns')}>
              Trade-In Submissions {tradeIns ? `(${tradeIns.length})` : ''}
            </TabButton>
          </div>

          {activityLoading ? (
            <p style={{ color: '#999', fontSize: 14, padding: '20px 0', textAlign: 'center' }}>Loading your activity…</p>
          ) : tab === 'requests' ? (
            (requests && requests.length > 0) ? (
              <div style={{ display: 'grid', gap: 12 }}>
                {requests.map((r) => (
                  <ActivityCard
                    key={r.id}
                    title={r.carName || [r.brandName, r.modelName].filter(Boolean).join(' ') || 'General Inquiry'}
                    subtitle={r.inquiryType}
                    status={r.status}
                    date={r.createdAt?.toDate?.()}
                  />
                ))}
              </div>
            ) : (
              <EmptyState
                text="No reservation or test drive requests yet."
                cta="Browse Cars"
                onCta={() => navigate('/shop')}
              />
            )
          ) : (
            (tradeIns && tradeIns.length > 0) ? (
              <div style={{ display: 'grid', gap: 12 }}>
                {tradeIns.map((t) => (
                  <ActivityCard
                    key={t.id}
                    title={`${t.carMake} ${t.carModel} (${t.carYear})`}
                    subtitle={t.condition || 'Trade-in submission'}
                    status={t.status}
                    date={t.createdAt?.toDate?.()}
                  />
                ))}
              </div>
            ) : (
              <EmptyState
                text="You haven't submitted a car to sell yet."
                cta="Sell Your Car"
                onCta={() => navigate('/sell-your-car')}
              />
            )
          )}
        </div>
      </div>

      <Footer />
      <BackToTop />
    </div>
  );
};

const Row: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #f0f0f0' }}>
    <span style={{ fontSize: 13, color: '#999' }}>{label}</span>
    <span style={{ fontSize: 14, color: '#222', fontWeight: 600 }}>{value}</span>
  </div>
);

const TabButton: React.FC<{ active: boolean; onClick: () => void; children: React.ReactNode }> = ({ active, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      background: 'none', border: 'none', cursor: 'pointer', padding: '8px 4px 12px',
      fontSize: 13, fontWeight: 600, color: active ? '#717fe0' : '#999',
      borderBottom: active ? '2px solid #717fe0' : '2px solid transparent', marginBottom: -1,
    }}
  >
    {children}
  </button>
);

const STATUS_COLORS: Record<string, string> = {
  'New Inquiry': '#1971c2', 'New Submission': '#1971c2',
  'Contacted': '#e8590c', 'In Review': '#e8590c',
  'Confirmed': '#2f9e44', 'Approved': '#2f9e44', 'Accepted': '#2f9e44',
  'Completed': '#2f9e44', 'Purchased': '#2f9e44',
  'Cancelled': '#c92a2a', 'Rejected': '#c92a2a', 'Declined': '#c92a2a',
};

const ActivityCard: React.FC<{ title: string; subtitle: string; status: string; date?: Date }> = ({ title, subtitle, status, date }) => (
  <div style={{
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
    border: '1px solid #eee', borderRadius: 8, padding: '14px 16px', flexWrap: 'wrap',
  }}>
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 14, fontWeight: 600, color: '#222' }}>{title}</div>
      <div style={{ fontSize: 12, color: '#999', marginTop: 2 }}>
        {subtitle}{date ? ` · ${date.toLocaleDateString()}` : ''}
      </div>
    </div>
    <span style={{
      fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 100, whiteSpace: 'nowrap',
      color: STATUS_COLORS[status] || '#666',
      background: `${STATUS_COLORS[status] || '#666'}18`,
    }}>
      {status}
    </span>
  </div>
);

const EmptyState: React.FC<{ text: string; cta: string; onCta: () => void }> = ({ text, cta, onCta }) => (
  <div style={{ textAlign: 'center', padding: '30px 10px' }}>
    <p style={{ color: '#999', fontSize: 14, marginBottom: 16 }}>{text}</p>
    <button
      type="button"
      onClick={onCta}
      style={{ padding: '9px 22px', background: '#717fe0', color: '#fff', border: 'none', borderRadius: 100, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
    >
      {cta}
    </button>
  </div>
);

export default Account;
